import {
  AppliedCoupon,
  ApplyCouponHandler,
  AssignedCoupon,
  RemoveCouponHandler,
  ShippingAddress,
} from '@app/types/cart/Coupon';
import { Box, Skeleton } from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks';
import type { UserInfo } from '@redbubble/next/types';
import { DeviceType } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import { useEffect, useRef, useState } from 'react';
import { AccordionTrigger } from './AccordionTrigger';
import { CouponInput } from './CouponInput/CouponInput';
import { DrawerTrigger } from './DrawerTrigger';
import { ManageCoupons } from './ManageCoupons/ManageCoupons';
import { MemberOffers } from './MemberOffers';

export interface ApplyCouponProps {
  appliedCoupon?: AppliedCoupon;
  assignedCoupons?: AssignedCoupon[];
  isLoading: boolean;
  hasServerError: boolean;
  isLoadingMemberOffers: boolean;
  doCouponCodeApply: ApplyCouponHandler;
  doCouponCodeRemove: RemoveCouponHandler;
  shippingAddress?: ShippingAddress;
}

export const ApplyCoupon = ({
  appliedCoupon,
  isLoading,
  hasServerError,
  assignedCoupons,
  isLoadingMemberOffers,
  doCouponCodeApply,
  doCouponCodeRemove,
  shippingAddress,
}: ApplyCouponProps) => {
  const abortController = useRef<AbortController>();
  // TODO: Avoid passing setDrawerOrAccordionClosed property 2 components deeper.
  const [drawerOrAccordionClosed, setDrawerOrAccordionClosed] = useState(true);
  const [isUsingManageCouponsComponent, setIsUsingManageCouponsComponent] =
    useState(false);

  const [{ locale }] = useUserInfo<UserInfo>();
  const couponInputFocusRef = useRef<HTMLInputElement>(null);
  const COUPON_INPUT_FOCUS_DELAY = 150;

  useEffect(() => {
    if (!drawerOrAccordionClosed) {
      setTimeout(() => {
        couponInputFocusRef.current?.focus();
      }, COUPON_INPUT_FOCUS_DELAY);
    }
  }, [drawerOrAccordionClosed]);

  useEffect(() => {
    if (drawerOrAccordionClosed)
      setIsUsingManageCouponsComponent(!!appliedCoupon);
  }, [appliedCoupon, drawerOrAccordionClosed]);

  const handleCouponAction = (coupon: AssignedCoupon, isApply: boolean) => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = undefined;
    }

    const newAbortController = new window.AbortController();
    abortController.current = newAbortController;

    if (isApply) {
      doCouponCodeApply({
        input: {
          couponCode: coupon.code,
          shippingAddress,
        },
        signal: abortController.current.signal,
      });
    } else {
      doCouponCodeRemove({
        input: { shippingAddress },
        signal: abortController.current.signal,
      });
    }
  };

  const result = useViewportWidth();
  const { isOrAbove } = result;
  const InnerContent = (
    // The tiny MarginX margins provide spacing for the focus border to appear around the input box
    <Box marginX="xxxs" marginTop="m">
      <CouponInput
        drawerOrAccordionClosed={drawerOrAccordionClosed}
        ref={couponInputFocusRef}
        appliedCoupon={appliedCoupon}
        assignedCoupons={assignedCoupons}
        doCouponCodeApply={doCouponCodeApply}
        doCouponCodeRemove={doCouponCodeRemove}
        shippingAddress={shippingAddress}
      />
      <MemberOffers
        isGetUserQueryServerError={hasServerError}
        assignedCoupons={assignedCoupons}
        appliedCoupon={appliedCoupon}
        onCouponAction={handleCouponAction}
        isLoading={isLoadingMemberOffers}
      />
    </Box>
  );

  if (isLoading) {
    return (
      <Skeleton
        height={40}
        animated
        roundedCorners
        data-testid="coupon-loading"
      />
    );
  }

  if (
    (appliedCoupon && drawerOrAccordionClosed) ||
    (isUsingManageCouponsComponent && !drawerOrAccordionClosed)
  ) {
    return (
      <ManageCoupons
        locale={locale}
        appliedCoupon={appliedCoupon}
        assignedCoupons={assignedCoupons}
        setDrawerOrAccordionClosed={setDrawerOrAccordionClosed}
        drawerOrAccordionClosed={drawerOrAccordionClosed}
        isGetUserQueryServerError={hasServerError}
        isLoadingMemberOffers={isLoadingMemberOffers}
        onCouponAction={handleCouponAction}
        doCouponCodeApply={doCouponCodeApply}
        doCouponCodeRemove={doCouponCodeRemove}
        shippingAddress={shippingAddress}
      />
    );
  }

  const assignedCount = assignedCoupons?.length || 0;
  const availableCoupons: number =
    !drawerOrAccordionClosed || appliedCoupon ? 0 : assignedCount;
  if (isOrAbove(DeviceType.Tablet)) {
    return (
      <AccordionTrigger
        setDrawerOrAccordionClosed={setDrawerOrAccordionClosed}
        drawerOrAccordionClosed={drawerOrAccordionClosed}
        availableCoupons={availableCoupons}
      >
        {InnerContent}
      </AccordionTrigger>
    );
  }

  return (
    <DrawerTrigger
      setDrawerOrAccordionClosed={setDrawerOrAccordionClosed}
      drawerOrAccordionClosed={drawerOrAccordionClosed}
      availableCoupons={availableCoupons}
    >
      {InnerContent}
    </DrawerTrigger>
  );
};
